import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Trophy, TrendingUp, Award, ArrowLeft, Crown, Medal } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await apiService.getLeaderboard();
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      toast.error('Failed to load leaderboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#4D96FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-semibold text-[#64748B]">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="leaderboard-page">
      {/* Header */}
      <div className="bg-white border-b-2 border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-[#64748B] hover:text-[#1E293B] font-semibold mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#FFC03D] to-orange-500 rounded-3xl flex items-center justify-center">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-[#1E293B]">Leaderboard</h1>
              <p className="text-lg text-[#64748B]">See how you rank against other students</p>
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            {/* 2nd Place */}
            <div className="flex flex-col items-center pt-12">
              <div className="w-20 h-20 bg-gradient-to-br from-slate-300 to-slate-400 rounded-3xl flex items-center justify-center mb-3 border-4 border-white shadow-xl">
                <Medal className="w-10 h-10 text-white" />
              </div>
              <div className="text-center bg-white rounded-2xl p-4 w-full border-2 border-slate-200">
                <p className="text-3xl font-black text-slate-600 mb-1">2</p>
                <p className="font-bold text-[#1E293B] truncate">{leaderboard[1]?.username}</p>
                <p className="text-sm text-[#64748B]">{leaderboard[1]?.elo_rating} ELO</p>
              </div>
            </div>

            {/* 1st Place */}
            <div className="flex flex-col items-center">
              <Crown className="w-12 h-12 text-[#FFC03D] mb-2 animate-bounce" />
              <div className="w-24 h-24 bg-gradient-to-br from-[#FFC03D] to-orange-500 rounded-3xl flex items-center justify-center mb-3 border-4 border-white shadow-2xl">
                <Trophy className="w-12 h-12 text-white" />
              </div>
              <div className="text-center bg-gradient-to-br from-[#FFC03D] to-orange-500 text-white rounded-2xl p-4 w-full shadow-xl">
                <p className="text-4xl font-black mb-1">1</p>
                <p className="font-bold truncate">{leaderboard[0]?.username}</p>
                <p className="text-sm opacity-90">{leaderboard[0]?.elo_rating} ELO</p>
              </div>
            </div>

            {/* 3rd Place */}
            <div className="flex flex-col items-center pt-12">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-300 to-orange-400 rounded-3xl flex items-center justify-center mb-3 border-4 border-white shadow-xl">
                <Award className="w-10 h-10 text-white" />
              </div>
              <div className="text-center bg-white rounded-2xl p-4 w-full border-2 border-slate-200">
                <p className="text-3xl font-black text-orange-600 mb-1">3</p>
                <p className="font-bold text-[#1E293B] truncate">{leaderboard[2]?.username}</p>
                <p className="text-sm text-[#64748B]">{leaderboard[2]?.elo_rating} ELO</p>
              </div>
            </div>
          </div>
        )}

        {/* Rest of Leaderboard */}
        <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#4D96FF] to-[#A493FF] p-6">
            <h2 className="text-2xl font-black text-white">All Rankings</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {leaderboard.map((entry, idx) => {
              const isCurrentUser = entry.username === user?.username;
              return (
                <div
                  key={idx}
                  data-testid={`leaderboard-entry-${idx + 1}`}
                  className={`p-5 flex items-center gap-4 hover:bg-slate-50 transition-colors ${
                    isCurrentUser ? 'bg-blue-50 border-l-4 border-[#4D96FF]' : ''
                  }`}
                >
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl ${
                    entry.rank === 1
                      ? 'bg-gradient-to-br from-[#FFC03D] to-orange-500 text-white'
                      : entry.rank === 2
                      ? 'bg-slate-300 text-white'
                      : entry.rank === 3
                      ? 'bg-orange-300 text-white'
                      : 'bg-slate-100 text-[#64748B]'
                  }`}>
                    {entry.rank}
                  </div>
                  <div className="flex-1">
                    <p className={`font-bold text-lg ${
                      isCurrentUser ? 'text-[#4D96FF]' : 'text-[#1E293B]'
                    }`}>
                      {entry.username} {isCurrentUser && '(You)'}
                    </p>
                    <p className="text-sm text-[#64748B]">
                      Level {entry.level} • {entry.questions_answered} questions
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-black text-[#1E293B]">{entry.elo_rating}</p>
                    <p className="text-xs text-[#64748B] font-semibold">ELO Rating</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#38E54D]">{entry.accuracy}%</p>
                    <p className="text-xs text-[#64748B] font-semibold">Accuracy</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;